/**
 * RiskDropdown Constants
 *
 * This file contains constants and utility functions for risk dropdowns.
 * Separated from component files to avoid React Fast Refresh warnings.
 *
 * Note: This file intentionally exports only constants and utility functions,
 * not React components. The react-refresh warning is disabled.
 */

/* eslint-disable react-refresh/only-export-components */

import {
  Bars2Icon,
  ChevronDoubleDownIcon,
  ChevronDoubleUpIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  ClockIcon,
} from '@heroicons/react/24/outline';
import { CircleCheck, LockKeyholeOpen, RefreshCcwDot } from 'lucide-react';

import { RiskStatus } from '@/types';
import { getAvailableStatuses } from '@/utils/availableStatuses.util';
import { getStatusLabel } from '@/utils/vulnerabilityStatus.util';

/**
 * Get all risk status options including Triaged.
 * Used as the source of truth for status configurations.
 */
function getAllRiskStatusOptions() {
  return [
    {
      label: getStatusLabel(RiskStatus.Triaged),
      value: RiskStatus.Triaged,
      icon: <ClockIcon className="size-4" />,
      count: '⌥ T',
    },
    {
      label: getStatusLabel(RiskStatus.Opened),
      value: RiskStatus.Opened,
      icon: <LockKeyholeOpen className="size-4" />,
      count: '⌥ O',
    },
    {
      label: getStatusLabel(RiskStatus.Remediated),
      value: RiskStatus.Remediated,
      icon: <RefreshCcwDot className="size-4" />,
      count: '⌥ C',
    },
    {
      label: getStatusLabel(RiskStatus.Accepted),
      value: RiskStatus.Accepted,
      icon: <CircleCheck className="size-4" />,
      count: '⌥ A',
    },
  ];
}

/**
 * Get base risk status options (all statuses available in the UI, with icons
 * and keyboard shortcuts).
 */
export function getBaseRiskStatusOptions() {
  const availableStatuses = getAvailableStatuses();
  const allOptions = getAllRiskStatusOptions();

  return allOptions.filter(option =>
    availableStatuses.includes(option.value as RiskStatus)
  );
}

export const BaseRiskStatusOptions = getBaseRiskStatusOptions();

export const riskSeverityOptions = [
  {
    label: 'Critical',
    value: 'C',
    icon: <ChevronDoubleUpIcon className="size-4 stroke-2" />,
  },
  {
    label: 'High',
    value: 'H',
    icon: <ChevronUpIcon className="size-4 stroke-2" />,
  },
  {
    label: 'Medium',
    value: 'M',
    icon: <Bars2Icon className="size-4 stroke-2" />,
  },
  {
    label: 'Low',
    value: 'L',
    icon: <ChevronDownIcon className="size-4 stroke-2" />,
  },
  {
    label: 'Info',
    value: 'I',
    icon: <ChevronDoubleDownIcon className="size-4" />,
  },
];
